import { NetworkTreeNode } from './types'

export function getChildren(
  parent: NetworkTreeNode,
  data: NetworkTreeNode[]
): NetworkTreeNode[] {
  return data.filter(
    node => node.viewableParent && node.viewableParent.id === parent.network.id
  )
}

export function hasChildren(
  node: NetworkTreeNode,
  data: NetworkTreeNode[]
): boolean {
  return data.some(
    child => child.viewableParent && child.viewableParent.id === node.network.id
  )
}

export function hasChildrenInFiltered(
  node: NetworkTreeNode,
  filteredData: NetworkTreeNode[]
): boolean {
  return filteredData.some(
    child => child.viewableParent && child.viewableParent.id === node.network.id
  )
}

export function getFilteredNodes(
  nodes: NetworkTreeNode[],
  searchTerm: string
): NetworkTreeNode[] {
  if (!searchTerm) return nodes

  const lowercaseSearch = searchTerm.toLowerCase()
  const matchingNodeIds: string[] = []
  const parentChainIds: string[] = []
  const childrenOfParentMatchesIds: string[] = []

  function findParentNode(parentId: string) {
    return nodes.find(n => n.network.id === parentId)
  }

  function findAllChildren(parentId: string): string[] {
    const childIds: string[] = []
    nodes.forEach(node => {
      if (node.viewableParent && node.viewableParent.id === parentId) {
        childIds.push(node.network.id)
        childIds.push(...findAllChildren(node.network.id))
      }
    })
    return childIds
  }

  function addParentChain(node: NetworkTreeNode) {
    let currentParent = node.viewableParent
    while (currentParent) {
      if (!parentChainIds.includes(currentParent.id)) {
        parentChainIds.push(currentParent.id)
      }
      const parentNode = findParentNode(currentParent.id)
      currentParent = parentNode?.viewableParent
    }
  }

  // Separate parent matches from child matches
  const parentMatches: NetworkTreeNode[] = []
  const childMatches: NetworkTreeNode[] = []

  nodes.forEach(node => {
    if (node.network.title.toLowerCase().includes(lowercaseSearch)) {
      matchingNodeIds.push(node.network.id)

      const nodeHasChildren = nodes.some(
        child =>
          child.viewableParent && child.viewableParent.id === node.network.id
      )

      if (nodeHasChildren) {
        parentMatches.push(node)
      } else {
        childMatches.push(node)
      }
    }
  })

  parentMatches.forEach(parentNode => {
    const allChildrenIds = findAllChildren(parentNode.network.id)
    allChildrenIds.forEach(childId => {
      if (!childrenOfParentMatchesIds.includes(childId)) {
        childrenOfParentMatchesIds.push(childId)
      }
    })
    // Also include parent chain for context
    addParentChain(parentNode)
  })

  // For child matches: only include parent chain (original behavior)
  childMatches.forEach(childNode => {
    addParentChain(childNode)
  })

  const filteredNodes = nodes.filter(
    node =>
      matchingNodeIds.includes(node.network.id) ||
      parentChainIds.includes(node.network.id) ||
      childrenOfParentMatchesIds.includes(node.network.id)
  )

  return filteredNodes
}

function hasAccessibleDescendants(
  node: NetworkTreeNode,
  data: NetworkTreeNode[]
): boolean {
  const children = getChildren(node, data)

  for (const child of children) {
    const isAccessible =
      ('isLtiAccessible' in child.network && child.network.isLtiAccessible) ||
      ('isAccessible' in child.network && child.network.isAccessible)
    if (isAccessible) {
      return true
    }

    if (hasAccessibleDescendants(child, data)) {
      return true
    }
  }

  return false
}

function isNodeAccessible(node: NetworkTreeNode): boolean {
  return (
    ('isLtiAccessible' in node.network && node.network.isLtiAccessible) ||
    ('isAccessible' in node.network && node.network.isAccessible) ||
    false
  )
}

function isAccessibilityCheckOff(disabledNodeIds: Set<string>): boolean {
  return disabledNodeIds.size === 0
}

export function shouldAutoExpand(
  node: NetworkTreeNode,
  disabledNodeIds: Set<string>,
  data: NetworkTreeNode[],
  searchTerm: string = ''
): boolean {
  // During search, don't auto-expand direct matches - let them start collapsed
  if (searchTerm && isDirectSearchMatch(node, searchTerm)) {
    return false
  }

  if (isAccessibilityCheckOff(disabledNodeIds)) {
    return true
  }

  const nodeIsAccessible = isNodeAccessible(node)
  const hasAccessibleChildren = hasAccessibleDescendants(node, data)
  const isNodeDisabled = disabledNodeIds.has(node.network.id)

  const isInteractableNode = !isNodeDisabled
  const isInaccessibleParentOfAccessibleChildren =
    !nodeIsAccessible && hasAccessibleChildren

  return isInteractableNode || isInaccessibleParentOfAccessibleChildren
}

export function canBeCollapsed(
  node: NetworkTreeNode,
  data: NetworkTreeNode[]
): boolean {
  const nodeIsAccessible = isNodeAccessible(node)
  const hasAccessibleChildren = hasAccessibleDescendants(node, data)

  // Accessible nodes can always be collapsed
  // Inaccessible nodes with accessible children cannot be collapsed
  return nodeIsAccessible || !hasAccessibleChildren
}

function isDirectSearchMatch(
  node: NetworkTreeNode,
  searchTerm: string
): boolean {
  if (!searchTerm) return false
  return node.network.title.toLowerCase().includes(searchTerm.toLowerCase())
}
